import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import { syncManager } from '../lib/syncManager';
import { fetchUserPreferences } from '../lib/userPreferences';
import { createPreferenceSync } from '../lib/preferenceSync';
import { onSignOut } from '../lib/signOutReset';
import {
  type VitalDef,
  type VitalReading,
  type CheckIn,
  type Symptom,
  DEFAULT_VITALS,
  DEFAULT_VITAL_ICON,
  latestReading,
  healthStreak,
  sumHealthXP,
} from '../utils/health';
import { toLocalYMD, shiftYMD } from '../utils/hydration';

const HISTORY_DAYS = 90;

interface CheckInRow { log_date: string; mood: number | null; energy: number | null; symptoms: Symptom[] | null; note: string | null }

/**
 * What a check-in save CHANGES — only the fields given are written; an absent one is
 * left as today's row has it (docs/DAY_LOG_STALE_WRITES.md). Symptoms have no input
 * any more, so they are never sent.
 */
export interface CheckInChanges { mood?: number | null; energy?: number | null; note?: string }

const rowToCheckIn = (c: CheckInRow): CheckIn => ({ date: c.log_date, mood: c.mood, energy: c.energy, symptoms: Array.isArray(c.symptoms) ? c.symptoms : [], note: c.note ?? '' });
const withCheckIn = (list: CheckIn[], c: CheckIn): CheckIn[] => [...list.filter(x => x.date !== c.date), c].sort((a, b) => a.date.localeCompare(b.date));
interface ReadingRow { id: string; vital_id: string; value: number; source: string; recorded_at: string }

interface HealthStore {
  vitals: VitalDef[];
  readings: VitalReading[];
  checkins: CheckIn[];
  // ⭐ Appointments are calendar EVENTS now (owner, 2026-10-05) — see
  // components/disciplines/health/AppointmentsPanel. `health_appointments` is no
  // longer read or written (left untouched: "start fresh").
  todayDate: string;

  isLoading: boolean;
  isInitialized: boolean;
  initializedUserId: string | null;

  initialize: () => Promise<void>;
  logReading: (vitalId: string, value: number, source?: string) => Promise<void>;
  addVital: (v: Omit<VitalDef, 'id' | 'active'>) => void;
  updateVital: (id: string, patch: Partial<Omit<VitalDef, 'id'>>) => void;
  deleteVital: (id: string) => void;
  toggleVitalActive: (id: string) => void;
  /** Resolves `true` when the database accepted it (the screen then shows the saved row). */
  saveCheckIn: (changes: CheckInChanges) => Promise<boolean>;

  latestByVital: (vitalId: string) => VitalReading | null;
  readingsForVital: (vitalId: string) => VitalReading[];
  todayCheckIn: () => CheckIn | null;
  getHealthXP: () => number;
  /** XP from the mood/energy check-in. Named for the ritual, not an area — the
   *  check-in has lived under Health, then Emotions, and now **Mind**. */
  getStreak: () => number;
  clearStore: () => void;
}

function buildDailyLog(checkins: CheckIn[], readings: VitalReading[]): { checkedIn: boolean; vitalsLogged: number }[] {
  const checkinDates = new Set(checkins.map(c => c.date));
  const vitalsByDate = new Map<string, Set<string>>();
  for (const r of readings) {
    const d = toLocalYMD(new Date(r.recordedAt));
    if (!vitalsByDate.has(d)) vitalsByDate.set(d, new Set());
    vitalsByDate.get(d)!.add(r.vitalId);
  }
  const dates = new Set<string>([...checkinDates, ...vitalsByDate.keys()]);
  return [...dates].map(d => ({ checkedIn: checkinDates.has(d), vitalsLogged: vitalsByDate.get(d)?.size ?? 0 }));
}

/**
 * The vitals are `user_preferences.health_vitals`, merged per vital on every write
 * (lib/preferenceSync.ts) — a stale tab can no longer put back one deleted
 * elsewhere or drop one added there.
 * ⚠️ An EMPTY saved list is the user's choice: only a never-saved one (null)
 * starts from the defaults. Deleting every vital used to bring them back.
 */
const vitalsSync = createPreferenceSync<VitalDef[]>({
  column: 'health_vitals',
  rule: { records: 'id' },
  decode: (saved) => (Array.isArray(saved) ? (saved as VitalDef[]) : DEFAULT_VITALS.map(v => ({ ...v }))),
  read: () => useHealthStore.getState().vitals,
  adopt: (vitals) => useHealthStore.setState({ vitals }),
  label: 'vitals',
  loadedFor: () => useHealthStore.getState().initializedUserId,
});

/** Await the pending vitals write (tests). */
export const flushHealthVitals = (): Promise<void> => vitalsSync.flush();

/**
 * ⚠️⚠️ MOVED BY `clearStore` (sign-out). A load or a save notes it before its first
 * await and drops its answer when it has moved: a read or a check-in still in flight
 * for the account that signed out must not land in the next account's empty store.
 */
let epoch = 0;

export const useHealthStore = create<HealthStore>((set, get) => ({
  vitals: DEFAULT_VITALS.map(v => ({ ...v })),
  readings: [],
  checkins: [],
  todayDate: toLocalYMD(new Date()),

  isLoading: false,
  isInitialized: false,
  initializedUserId: null,

  initialize: async () => {
    if (get().isLoading) return;
    const at = epoch;
    const { user } = await syncManager.safeAuthCheck();
    if (!user || at !== epoch) return;
    if (get().initializedUserId === user.id) return;

    set({ isLoading: true });
    try {
      const prefs = await fetchUserPreferences();

      const todayStr = toLocalYMD(new Date());
      const sinceDate = shiftYMD(todayStr, -HISTORY_DAYS);
      const sinceTs = `${sinceDate}T00:00:00Z`;

      const [readingsRes, checkinsRes] = await Promise.all([
        supabase.from('health_readings').select('*').eq('user_id', user.id).gte('recorded_at', sinceTs).order('recorded_at', { ascending: true }),
        supabase.from('health_checkins').select('*').eq('user_id', user.id).gte('log_date', sinceDate).order('log_date', { ascending: true }),
      ]);
      // Signed out while reading: the next account must not get this one's check-ins —
      // nor its vitals as the list's base (so the list is loaded only after this).
      if (at !== epoch) return;
      if (readingsRes.error) throw readingsRes.error;
      if (checkinsRes.error) throw checkinsRes.error;

      const vitals = vitalsSync.load(prefs?.row?.health_vitals ?? null, prefs?.row?.updated_at);

      const readings: VitalReading[] = (readingsRes.data ?? []).map((r: ReadingRow) => ({ id: r.id, vitalId: r.vital_id, value: Number(r.value), source: r.source, recordedAt: r.recorded_at }));
      const checkins: CheckIn[] = (checkinsRes.data ?? []).map(rowToCheckIn);

      set({ vitals, readings, checkins, todayDate: todayStr, isLoading: false, isInitialized: true, initializedUserId: user.id });
    } catch (err) {
      if (at !== epoch) return; // the next account's load may be running: leave its flag and base alone
      console.error('Error initializing health:', err);
      vitalsSync.reset(); // the list was never adopted — its base must not stand either
      set({ isLoading: false });
    }
  },

  logReading: async (vitalId, value, source = 'manual') => {
    const at = epoch;
    const { user } = await syncManager.safeAuthCheck();
    if (!user || at !== epoch) return;
    const recordedAt = new Date().toISOString();
    const local: VitalReading = { id: crypto.randomUUID(), vitalId, value, source, recordedAt };
    set(state => ({ readings: [...state.readings, local] }));
    try {
      const { error } = await supabase.from('health_readings').insert({ user_id: user.id, vital_id: vitalId, value, source, recorded_at: recordedAt });
      if (error) throw error;
    } catch (err) {
      console.error('Error logging reading:', err);
    }
  },

  addVital: (v) => persistVitals(set, get, [...get().vitals, { id: crypto.randomUUID(), name: v.name.trim() || 'Vital', unit: v.unit.trim(), icon: v.icon || DEFAULT_VITAL_ICON, targetMin: v.targetMin, targetMax: v.targetMax, active: true }]),
  updateVital: (id, patch) => persistVitals(set, get, get().vitals.map(v => (v.id === id ? { ...v, ...patch } : v))),
  deleteVital: (id) => persistVitals(set, get, get().vitals.filter(v => v.id !== id)),
  toggleVitalActive: (id) => persistVitals(set, get, get().vitals.map(v => (v.id === id ? { ...v, active: !v.active } : v))),

  /* ⚠️⚠️ ONLY THE CHANGED FIELDS. It used to upsert all four from the form, so a blank or
     stale form (today's check-in saved on another device, or not loaded yet) that only
     set a mood nulled the energy and the note — and wiped the symptoms. An upsert of a
     subset updates ONLY those columns on conflict (PostgREST merge-duplicates), so no
     read or compare-and-swap is needed: the columns are independent. */
  saveCheckIn: async (changes) => {
    const at = epoch;
    const { user } = await syncManager.safeAuthCheck();
    if (!user || at !== epoch) return false;
    const todayStr = toLocalYMD(new Date());
    const cols: Record<string, unknown> = {};
    if (changes.mood !== undefined) cols.mood = changes.mood;
    if (changes.energy !== undefined) cols.energy = changes.energy;
    if (changes.note !== undefined) cols.note = changes.note;
    if (Object.keys(cols).length === 0) return true;

    const before = get().checkins.find(c => c.date === todayStr);
    const shown: CheckIn = { date: todayStr, mood: null, energy: null, symptoms: [], note: '', ...before, ...changes };
    set(state => ({ todayDate: todayStr, checkins: withCheckIn(state.checkins, shown) }));
    try {
      const { data, error } = await supabase
        .from('health_checkins')
        .upsert({ user_id: user.id, log_date: todayStr, ...cols, updated_at: new Date().toISOString() }, { onConflict: 'user_id,log_date' })
        .select('log_date, mood, energy, symptoms, note')
        .single();
      // Signed out meanwhile: the saved (or refused) row is that account's — not shown to the next.
      if (at !== epoch) return !error && Boolean(data);
      if (error || !data) throw error ?? new Error('no row returned');
      // The saved row — with whatever another device put in the fields not sent here.
      set(state => ({ checkins: withCheckIn(state.checkins, rowToCheckIn(data as CheckInRow)) }));
      return true;
    } catch (err) {
      if (at !== epoch) return false;
      console.error('Error saving check-in:', err);
      set(state => ({ checkins: before ? withCheckIn(state.checkins, before) : state.checkins.filter(c => c.date !== todayStr) }));
      return false;
    }
  },

  latestByVital: (vitalId) => latestReading(get().readings.filter(r => r.vitalId === vitalId)),
  readingsForVital: (vitalId) => get().readings.filter(r => r.vitalId === vitalId).sort((a, b) => a.recordedAt.localeCompare(b.recordedAt)),
  todayCheckIn: () => get().checkins.find(c => c.date === get().todayDate) ?? null,
  // Health = vitals only now; the mood/energy check-in feeds the Emotions area.
  // ⭐ Vitals AND the check-in (2026-09-30): the check-in moved from Mind into the
  // period tracker, which lives in Health, so its XP comes home — a check-in day
  // earns the full daily cap, a vitals-only day the ratio (`dayHealthXP`).
  getHealthXP: () => sumHealthXP(buildDailyLog(get().checkins, get().readings)),
  getStreak: () => healthStreak(get().checkins.map(c => c.date), get().todayDate, shiftYMD),

  /**
   * ⚠️⚠️ MOOD, ENERGY AND VITALS DO NOT OUTLIVE THE ACCOUNT. No `persist`, but a sign-out
   * does not reload the page: without this the check-ins, readings and vitals stayed in
   * memory and the next account's first render showed them until its own load answered.
   */
  clearStore: () => {
    epoch += 1;
    vitalsSync.reset();
    set({ vitals: DEFAULT_VITALS.map(v => ({ ...v })), readings: [], checkins: [], todayDate: toLocalYMD(new Date()), isLoading: false, isInitialized: false, initializedUserId: null });
  },
}));

function persistVitals(set: (p: Partial<HealthStore>) => void, get: () => HealthStore, vitals: VitalDef[]) {
  vitalsSync.recordBase(get().vitals);
  set({ vitals });
  vitalsSync.schedule();
}

onSignOut(() => useHealthStore.getState().clearStore());
